import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Coffee, Check, Archive, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLE = {
  nouă: "bg-[var(--tint-pink)] text-[var(--accent-dark)]",
  plătită: "bg-[var(--tint-mint)] text-[var(--teal-dark)]",
  închisă: "bg-[var(--tint-butter)] text-[var(--sun-dark)]",
};

const fmt = (iso) => new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });

export const AdminRequestsPage = () => {
  const [items, setItems] = useState(null);

  const load = () =>
    api.get("/admin/requests").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e, "Nu am putut încărca cererile")));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/requests/${id}`, { status });
      setItems((list) => list.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut actualiza"));
    }
  };

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Ștergi cererea definitiv?")) return;
    try {
      await api.delete(`/admin/requests/${id}`);
      setItems((list) => list.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut șterge"));
    }
  };

  return (
    <main data-testid="admin-requests" className="mx-auto max-w-4xl px-6 py-12">
      <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Cumpără-mi o cafea</p>
        <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Cererile primite</h1>
        <p className="mt-2 text-sm text-[#F7F1E8]/78">Fiecare persoană a primit pe e-mail variantele tale de plată. Când ajung banii, marchează cererea ca plătită.</p>
      </div>

      {items === null ? (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68">Se încarcă…</p>
      ) : items.length === 0 ? (
        <div data-testid="requests-empty" className="mt-10 rounded-3xl bg-[var(--tint-butter)] p-12 text-center">
          <Coffee className="mx-auto h-8 w-8 text-[var(--sun-dark)]" strokeWidth={1.4} />
          <p className="mt-4 font-serif text-2xl text-[#F7F1E8]">Nicio cerere încă.</p>
          <p className="mt-2 text-sm text-[#F7F1E8]/78">Când cineva alege o variantă pe pagina „Cumpără-mi o cafea”, apare aici.</p>
        </div>
      ) : (
        <ul className="mt-10 space-y-4" data-testid="requests-list">
          {items.map((r) => (
            <li key={r.id} data-testid={`request-${r.id}`} className="rounded-2xl bg-[#2B1B13] p-6 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${STATUS_STYLE[r.status] || ""}`} data-testid={`request-status-${r.id}`}>
                      {r.status}
                    </span>
                    <span className="text-xs text-[#F7F1E8]/68">{fmt(r.created_at)}</span>
                  </div>
                  <p className="mt-2 font-serif text-xl text-[#F7F1E8]">
                    {r.label} · <strong>{r.amount_lei} lei</strong>
                  </p>
                  <p className="mt-1 text-sm text-[#F7F1E8]/86">
                    {r.name} · <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 underline decoration-dotted"><Mail className="h-3 w-3" strokeWidth={1.6} />{r.email}</a>
                  </p>
                  {r.message && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#F7F1E8]/90">{r.message}</p>}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {r.status !== "plătită" && (
                    <button type="button" onClick={() => setStatus(r.id, "plătită")} data-testid={`request-paid-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--teal)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white">
                      <Check className="h-3.5 w-3.5" strokeWidth={2} /> Plătită
                    </button>
                  )}
                  {r.status !== "închisă" && (
                    <button type="button" onClick={() => setStatus(r.id, "închisă")} data-testid={`request-close-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--milk)_20%,transparent)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--milk)]">
                      <Archive className="h-3.5 w-3.5" strokeWidth={1.8} /> Închide
                    </button>
                  )}
                  <button type="button" onClick={() => remove(r.id)} data-testid={`request-delete-${r.id}`} aria-label="Șterge" className="rounded-full p-2 text-[#F7F1E8]/58 hover:bg-[var(--tint-pink)] hover:text-[var(--accent-dark)]">
                    <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
};

export default AdminRequestsPage;
